// API del módulo CROSS `tasks` (lealify-base /api/tasks, compartido entre verticales).
// Migrado de /api/letsmove/tasks el 2026-07-01: mismas operaciones + plantillas
// recurrentes (diaria/semanal/mensual) + omitir-con-motivo + responsables.
import { api } from '../../services/apiClient';

export interface Task {
  task_id: number;
  template_id: number | null;      // != null → instancia de plantilla recurrente
  title: string;
  description: string | null;
  status: 'open' | 'done' | 'skipped';
  priority: 'normal' | 'high';
  complete_by: string | null;      // 'YYYY-MM-DD'
  assigned_user_id: number | null;
  assignee: string | null;
  days_until: number | null;       // <0 vencida, 0 hoy, >0 futura, null sin fecha
  skip_reason: string | null;
  created_at: string | null;
  done_at: string | null;
  done_by: number | null;
  done_by_name: string | null;     // quién la completó/omitió
  // Foto-evidencia (F4, 17-ago): el candado real vive en el backend (done sin foto = 400).
  requires_photo: 0 | 1;
  photo_path: string | null;       // URL servida con la cookie de sesión (misma origin)
}
export interface TasksSummary { open: number; overdue: number; today: number; }

export type Recur = 'daily' | 'weekly' | 'monthly';
export interface TaskTemplate {
  template_id: number;
  title: string;
  description: string | null;
  priority: 'normal' | 'high';
  recur: Recur;
  weekdays: string | null;         // CSV 0=Dom..6=Sáb (recur=weekly)
  month_day: number | null;        // 1..31 (recur=monthly)
  assigned_user_id: number | null;
  assignee: string | null;
  active: 0 | 1;
  created_at: string | null;
  requires_photo: 0 | 1;              // F4: exige foto de evidencia al completar
}
export interface Assignee { id: number; name: string; role: string; }

export function listTasks(scope: 'home' | 'all' = 'home', mine = false) {
  return api.get<{ data: Task[]; summary: TasksSummary }>(`/tasks?scope=${scope}${mine ? '&mine=1' : ''}`);
}

/** TABLERO SEMANAL (Karla 29-jul). `week` = cualquier fecha dentro de la semana (lunes→domingo).
 *  Cada tarea trae su `bucket`: arrastrada de semanas pasadas · de esta semana · hecha · más adelante. */
export interface WeekTasksResp {
  data: (Task & { week_anchor: string | null; bucket: 'carried' | 'week' | 'done' | 'later' })[];
  week: { from: string; to: string };
  summary: TasksSummary & { carried: number; done: number; later: number };
}
export function listWeekTasks(week?: string, mine = false) {
  return api.get<WeekTasksResp>(`/tasks?scope=week${week ? `&week=${week}` : ''}${mine ? '&mine=1' : ''}`);
}
export function listAssignees() {
  return api.get<{ data: Assignee[] }>('/tasks/assignees');
}
export interface TaskInput {
  title: string; description?: string | null; complete_by?: string | null;
  priority?: 'normal' | 'high'; assigned_user_id?: number | null;
}
export function createTask(payload: TaskInput) {
  return api.post<{ ok: true; task_id: number }>('/tasks', payload);
}
export function updateTask(id: number, patch: Partial<TaskInput> & { status?: Task['status']; skip_reason?: string }) {
  return api.patch<{ ok: true }>(`/tasks/${id}`, patch);
}
export function deleteTask(id: number) {
  return api.del<{ ok: true }>(`/tasks/${id}`);
}

// ── Foto-evidencia (F4, 17-ago) — mismos endpoints que usa la agenda dental ──
export function uploadTaskPhoto(id: number, image: string) {
  return api.post<{ ok: true; photo_path: string }>(`/tasks/${id}/photo`, { image });
}
export function deleteTaskPhoto(id: number) {
  return api.del<{ ok: true }>(`/tasks/${id}/photo`);
}
/** Token de 20 min para que el celular suba la foto SIN sesión (/evidencia/:token). */
export function evidenceLink(id: number) {
  return api.post<{ token: string; minutes: number }>(`/tasks/${id}/evidence-link`);
}

export interface TemplateInput {
  title: string; description?: string | null; priority?: 'normal' | 'high';
  recur: Recur; weekdays?: string | null; month_day?: number | null;
  assigned_user_id?: number | null;
  requires_photo?: boolean | 0 | 1;   // F4: la instancia del día exigirá foto para completarse
}
export function listTemplates() {
  return api.get<{ data: TaskTemplate[] }>('/tasks/templates');
}
export function createTemplate(payload: TemplateInput) {
  return api.post<{ ok: true; template_id: number }>('/tasks/templates', payload);
}
export function updateTemplate(id: number, patch: Partial<TemplateInput> & { active?: 0 | 1 }) {
  return api.patch<{ ok: true }>(`/tasks/templates/${id}`, patch);
}
export function deleteTemplate(id: number) {
  return api.del<{ ok: true }>(`/tasks/templates/${id}`);
}

/** Descripción legible de la recurrencia (idea del POS). */
export const DIAS_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
export function recurLabel(t: Pick<TaskTemplate, 'recur' | 'weekdays' | 'month_day'>): string {
  if (t.recur === 'daily') return 'Todos los días';
  if (t.recur === 'weekly') {
    const dias = (t.weekdays || '').split(',').map(Number).filter((n) => n >= 0 && n <= 6);
    return dias.length ? `Cada ${dias.map((d) => DIAS_CORTOS[d]).join(', ')}` : 'Semanal';
  }
  return `Día ${t.month_day} de cada mes`;
}
